import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  SERVICE_AUDIT_ACTION_LABELS,
  SERVICE_AUDIT_BADGE_CLASSES,
  SERVICE_AUDIT_BADGE_TONES,
  SERVICE_AUDIT_FALLBACK_BADGE_TONE,
  SERVICE_AUDIT_CHANGE_LABELS,
  SERVICE_AUDIT_ENTITY_LABELS,
} from "../../lib/serviceConstants";
import {
  PassiveBadge,
  displayValue,
  formatPresentationTimestamp,
} from "../ui/data-presentation";

const present = (value) => value !== null && value !== undefined && value !== "";
const display = (value) => String(displayValue(value));
const humanize = (value) => display(value).replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

const displayChangeValue = (value) => {
  if (!present(value)) return "—";
  if (value === "[redacted]") return "[redacted]";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    const values = value
      .filter((item) => item === null || ["string", "number", "boolean"].includes(typeof item))
      .map((item) => displayChangeValue(item));
    return values.length > 0 ? values.join(", ") : "—";
  }
  if (typeof value === "object") return "—";
  return String(value);
};

const normalizeAuditChanges = (changes) => {
  if (!Array.isArray(changes)) return [];
  return changes.flatMap((change) => {
    if (!change || typeof change !== "object" || Array.isArray(change)) return [];
    const field = typeof change.field === "string" ? change.field : "";
    const fallbackLabel = SERVICE_AUDIT_CHANGE_LABELS[field];
    if (!fallbackLabel) return [];
    const apiLabel = typeof change.label === "string" ? change.label.trim() : "";
    const forceRedaction = change.is_redacted === true || field === "tracking_number" || field === "technician_id";
    return [{
      field,
      label: apiLabel || fallbackLabel,
      previousValue: forceRedaction ? "[redacted]" : displayChangeValue(change.previous_value),
      newValue: forceRedaction ? "[redacted]" : displayChangeValue(change.new_value),
    }];
  });
};

export function formatServiceAuditTimestamp(value) {
  return formatPresentationTimestamp(value);
}

function Value({ children }) {
  const redacted = children === "[redacted]";
  return (
    <span className={redacted ? "text-zinc-500 italic" : "text-zinc-200"}>
      {display(children)}
    </span>
  );
}

function DetailGrid({ items, testId }) {
  const visible = items.filter((item) => present(item.value));
  if (visible.length === 0) return null;
  return (
    <dl className="grid gap-3 sm:grid-cols-2" data-testid={testId}>
      {visible.map((item) => (
        <div key={item.label} className="rounded-sm border border-zinc-800 bg-zinc-900/60 px-3 py-2.5">
          <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-500">
            {item.label}
          </dt>
          <dd className="mt-1 break-words text-sm"><Value>{item.value}</Value></dd>
        </div>
      ))}
    </dl>
  );
}

function Section({ title, children, testId }) {
  if (!children) return null;
  return (
    <section className="space-y-3" data-testid={testId}>
      <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-zinc-400">{title}</h3>
      {children}
    </section>
  );
}

function DetailSection({ title, items, testId }) {
  if (!items.some((item) => present(item.value))) return null;
  return (
    <Section title={title} testId={testId}>
      <DetailGrid items={items} />
    </Section>
  );
}

function Relationship({ title, relationship }) {
  if (!relationship || typeof relationship !== "object") return null;
  return (
    <div className="rounded-sm border border-zinc-800 bg-zinc-900/60 p-3">
      <div className="text-xs font-semibold text-zinc-300">{title}</div>
      <DetailGrid
        items={[
          { label: "Hold Status", value: relationship.hold_status },
          { label: "Usage Status", value: relationship.usage_status },
          { label: "Hold Linked to Ticket", value: present(relationship.hold_linked_to_ticket) ? (relationship.hold_linked_to_ticket ? "Yes" : "No") : null },
          { label: "Usage Linked to Ticket", value: present(relationship.usage_linked_to_ticket) ? (relationship.usage_linked_to_ticket ? "Yes" : "No") : null },
        ]}
      />
    </div>
  );
}

export default function ServiceAuditDetailDialog({ record, open, onOpenChange }) {
  const details = record?.details && typeof record.details === "object" ? record.details : {};
  const actionLabel = record?.action_label || SERVICE_AUDIT_ACTION_LABELS[record?.action] || "Service Audit Event";
  const badgeTone = SERVICE_AUDIT_BADGE_TONES[record?.action] || SERVICE_AUDIT_FALLBACK_BADGE_TONE;
  const badgeClass = badgeTone === "custom" ? SERVICE_AUDIT_BADGE_CLASSES[record?.action] : undefined;
  const changes = Array.isArray(details.changes) ? details.changes : [];
  const validChanges = normalizeAuditChanges(changes);
  const reconciliationItems = [
    { label: "Reconciliation Requested", value: present(details.reconciliation_requested) ? (details.reconciliation_requested ? "Yes" : "No") : null },
    { label: "Automatic Reversal", value: present(details.automatic_reversal) ? (details.automatic_reversal ? "Yes" : "No") : null },
    { label: "Holds Converted to Used", value: details.holds_converted_to_used_count },
    { label: "Usages Reversed to Hold", value: details.usages_reversed_to_hold_count },
    { label: "Retained Active Usage", value: details.retained_active_usage_count },
    { label: "Created Usage", value: details.created_usage_count },
    { label: "Reconciliation Action", value: details.reconciliation_action },
    { label: "Reconciliation Context", value: details.reconciliation_context },
  ];
  const hasReconciliation = reconciliationItems.some((item) => present(item.value));
  const hasRelationships = details.previous_relationship || details.new_relationship;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flowpi-dialog flowpi-dialog--inspection max-w-3xl gap-0 border-zinc-800 bg-zinc-950 p-0 text-zinc-100" data-flowpi-dialog-size="inspection" data-testid="service-audit-detail-dialog">
        <DialogHeader className="border-b border-zinc-800 px-6 py-5 pr-14">
          <div className="flex flex-wrap items-center gap-3">
            <PassiveBadge tone={badgeTone} className={badgeClass}>
              {actionLabel}
            </PassiveBadge>
            <DialogTitle>{actionLabel}</DialogTitle>
          </div>
          <DialogDescription className="pt-1 text-zinc-400">
            {record?.summary || actionLabel}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[calc(100vh-10rem)] space-y-6 overflow-y-auto px-6 py-5">
          <DetailSection title="Metadata" testId="service-audit-metadata" items={[
              { label: "Timestamp", value: formatServiceAuditTimestamp(record?.timestamp) },
              { label: "Actor", value: displayValue(record?.actor) },
              { label: "Ticket Number", value: displayValue(record?.ticket_number) },
              { label: "Entity Type", value: SERVICE_AUDIT_ENTITY_LABELS[record?.entity_type] || humanize(record?.entity_type) },
              { label: "Entity Reference", value: displayValue(record?.entity_reference) },
            ]} />

          <DetailSection title="Ticket Snapshot" items={[
              { label: "Category", value: details.category },
              { label: "Branch", value: details.branch },
              { label: "Stage", value: details.stage },
            ]} />

          <DetailSection title="Stage Transition" items={[
              { label: "From Stage", value: details.from_stage },
              { label: "To Stage", value: details.to_stage },
              { label: "Transition Type", value: details.transition_type },
            ]} />

          {validChanges.length > 0 ? (
            <Section title="Changes" testId="service-audit-changes">
              <div className="overflow-x-auto rounded-sm border border-zinc-800">
                <table className="w-full min-w-[520px] border-collapse text-left text-xs">
                  <thead className="bg-zinc-900 text-zinc-400">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Field</th>
                      <th className="px-3 py-2 font-semibold">Previous Value</th>
                      <th className="px-3 py-2 font-semibold">New Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800 bg-zinc-900/60">
                    {validChanges.map((change, index) => (
                      <tr key={`${change.field}-${index}`}>
                        <th className="px-3 py-2.5 font-semibold text-zinc-300">{change.label}</th>
                        <td className="break-words px-3 py-2.5"><Value>{change.previousValue}</Value></td>
                        <td className="break-words px-3 py-2.5"><Value>{change.newValue}</Value></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          ) : null}

          {hasReconciliation ? (
            <Section title="Reconciliation" testId="service-audit-reconciliation">
              <DetailGrid items={reconciliationItems} />
            </Section>
          ) : null}

          <DetailSection title="Status and Repair Action" items={[
              { label: "Previous Status", value: details.previous_status },
              { label: "New Status", value: details.new_status },
              { label: "Previous Repair Action", value: details.previous_repair_action },
              { label: "New Repair Action", value: details.new_repair_action },
            ]} />

          <DetailSection title="Spare Part References" items={[
              { label: "Usage Number", value: details.usage_number },
              { label: "Part Number", value: details.part_number },
              { label: "Part Name", value: details.part_name },
            ]} />

          {hasRelationships ? (
            <Section title="Relationships" testId="service-audit-relationships">
              <div className="grid gap-3 sm:grid-cols-2">
                <Relationship title="Previous Relationship" relationship={details.previous_relationship} />
                <Relationship title="New Relationship" relationship={details.new_relationship} />
              </div>
            </Section>
          ) : null}

          {present(details.reason) ? (
            <Section title="Reason" testId="service-audit-reason">
              <p className="whitespace-pre-wrap break-words rounded-sm border border-zinc-800 bg-zinc-900/60 p-3 text-sm text-zinc-200">
                {details.reason}
              </p>
            </Section>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
